import React, { useRef, useState } from 'react';
import lodash from 'lodash';
import * as Yup from 'yup';
import setLocalValue from '../utils/setLocalValue';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../utils/snackbar';
import { logEvent } from '../utils/analytics';
import { IN_DEVELOPMENT } from '../constants';
import EVENTS from '../constants/events';
import redcatApiFetch from '../api';
import getRegisterProfileFields from '../selectors/getRegisterProfileFields';
import getRegisterProfileSchema from '../selectors/getRegisterProfileSchema';
import getRegisterProfileInitialValues from '../selectors/getRegisterProfileInitialValues';
import getConfig from '../selectors/getConfig';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { SIGN_IN_MODAL_ID } from '../modals/SignInModal';
import StandardButton from '../components/StandardButton';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import { useTranslation } from 'react-i18next';
import getDeviceTypeMobile from '../selectors/getDeviceTypeMobile';
import { registerUser } from '../utils/analytics';
import { OrderingSelectors } from 'polygon-ordering';
import type { FormikHelpers } from 'formik';
import { Formik, Form } from 'formik';
import RegistrationFields from '../components/RegistrationFields';
import { syncMember } from '../thunks/syncMember';
import FormTextField from '../components/FormTextField';
import { useLocation } from 'react-router-dom';
import { INPUT_FIELDS } from '../constants/inputfields';
import history from '../history';
import ScreenLink from './ScreenLink';
import i18next from 'i18next';
import {
  useRedcatAnalytics,
  trackSignupSubmitted,
  trackSignupFailed,
  trackSigninStarted,
} from '../analytics';
import useWebOtpAutofill from '../hooks/useWebOtpAutofill';

const { getOrderTotals, getStagedPurchases, getLocation } = OrderingSelectors;

// Formik's render-prop body isn't itself a function component, so hooks can't be called
// directly inside it - this wrapper gives useWebOtpAutofill a real component to live in.
const MfaCodeField = ({
  label,
  onChange,
  setFieldValue,
}: {
  label: string;
  onChange: (e: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: unknown) => void;
}) => {
  useWebOtpAutofill(true, code => setFieldValue('mfaCode', code));

  return (
    <FormTextField
      id="mfaCode"
      name="mfaCode"
      type="text"
      label={label}
      required
      autoFocus
      onChange={onChange}
      slotProps={{
        htmlInput: {
          autoComplete: 'one-time-code',
          inputMode: 'numeric',
          pattern: '[0-9]*',
        },
      }}
    />
  );
};

const RegisterForm = () => {
  const { t } = useTranslation();
  const pathname = useLocation();
  const registrationFields = useAppSelector(getRegisterProfileFields);
  const registratonFieldSchema = useAppSelector(getRegisterProfileSchema);
  const registrationFieldInitialValues = useAppSelector(state =>
    getRegisterProfileInitialValues(state, pathname),
  );
  const config = useAppSelector(getConfig);
  const dispatch = useAppDispatch();
  const deviceTypeMobile = useAppSelector(getDeviceTypeMobile);
  const enableReCaptcha = useAppSelector(state => state.ordering.config.enableReCaptcha);
  const { reCaptchaSiteKey } = config;
  const orderTotals = useAppSelector(getOrderTotals);
  const stagedPurchases = useAppSelector(getStagedPurchases);
  const location = useAppSelector(getLocation);
  const { track } = useRedcatAnalytics();
  const flowStartedAt = useRef<number>(Date.now());
  const marketingEmailFieldId = registrationFields?.visible.find(
    field => field.inputType === 'sendemail',
  )?.id;
  const marketingSmsFieldId = registrationFields?.visible.find(
    field => field.inputType === 'sendsms',
  )?.id;
  //recaptcha settings
  const [reCaptchaToken, setReCaptchaToken] = useState<string | null>(null);
  //tfa
  const [mfaToken, setMfaToken] = useState(null);
  const onReCaptchaChange = (reCaptchaToken: string | null) => {
    setReCaptchaToken(reCaptchaToken);
  };

  const onReCaptchaError = () => {
    clearReCaptchaToken();

    enqueueErrorSnackbar(t('problemWithReCaptchaMessage'));
  };

  const clearReCaptchaToken = () => setReCaptchaToken(null);

  const confirmRegistrationFieldMode = registrationFields?.visible.find(
    field => field.id === 'ConfirmPassword',
  )?.mode;

  const submit = (
    values: Partial<FormikFields>,
    { setSubmitting, resetForm }: FormikHelpers<FormikFields>, //{ setSubmitting: (isSubmitting: boolean) => void },
  ) => {
    let sentValues = {
      ...values,
      ...(values['RegistrationCode'] ? { RegistrationCode: values['RegistrationCode'] } : {}),
      ...(values['Referrer'] ? { Referrer: values['Referrer'] } : {}),
      ...(confirmRegistrationFieldMode === 'optional' ? { ConfirmPassword: values.Password } : {}),
      ...(values['MemberNo'] ? { MemberNo: values['MemberNo'].replace(/\s/g, '') } : {}),
      ...(values['VerifyPIN'] ? { VerifyPIN: values['VerifyPIN'].replace(/\s/g, '') } : {}),
    };

    const MOBILE_REG_NZ = /(^02|^\+642|^03|^\+643)([\d]{7,10})$/;
    const regNzMobile = new RegExp(MOBILE_REG_NZ);
    const { Mobile } = sentValues;
    if (Mobile) {
      if (regNzMobile.test(Mobile)) {
        if (String(Mobile).startsWith('0')) {
          sentValues = { ...sentValues, Mobile: '+64' + String(Mobile).slice(1) };
        }
      }
    }

    if (values['Terms' as keyof FormikFields] === true)
      delete sentValues['Terms' as keyof FormikFields];
    //register api call
    redcatApiFetch({
      path: 'api/v1/register',
      method: 'POST',
      body: mfaToken
        ? {
            ...lodash.omit(sentValues, 'mfaCode'),
            'g-recaptcha-response': reCaptchaToken,
            //@ts-ignore
            tfa_pin: sentValues.mfaCode,
            tfa_token: mfaToken,
          }
        : lodash.omit({ ...sentValues, 'g-recaptcha-response': reCaptchaToken }, 'mfaCode'),
    })
      .then((result: any) => {
        setMfaToken(null);
        registerUser({
          content_name: 'native',
          status: result.success,
          value: orderTotals,
          items: stagedPurchases.map(purchaseItem => {
            return {
              ...purchaseItem.item,
              totalPrice: purchaseItem.moneyPrice,
            };
          }),
          userData: result.data,
          store: location?.name,
        });

        //register successfully
        resetForm({ values });
        setSubmitting(false);
        logEvent(EVENTS.MEMBER_REGISTER); // log event
        clearReCaptchaToken();

        if (IN_DEVELOPMENT && result.token) {
          setLocalValue('memberAuthToken', result.token);
        }

        dispatch(syncMember());
        dispatch(setCurrentModal(null));
        history.push('/registration_verify', {
          isRegistration: true,
        });
      })
      .catch((e: any) => {
        setSubmitting(false);
        clearReCaptchaToken();
        const data = lodash.get(e, 'details.json', {});
        if (data.error_code === 3) {
          setMfaToken(data.additional_info);
          enqueueSuccessSnackbar(i18next.t(`errorCode.${data.error_code}`));
        } else {
          logEvent(EVENTS.MEMBER_REGISTER_FAILED);
          const extraInfo = lodash.get(data, 'extra_info') || lodash.get(e, 'extra_info') || [];
          const fieldPaths = extraInfo
            .map((info: { path: string[] }) => info.path.join(' > '))
            .join(', ');
          trackSignupFailed(track, {
            errorField: fieldPaths || data.error || lodash.get(e, 'error') || null,
            errorCode: data.error_code ?? lodash.get(e, 'error_code') ?? null,
          });
          enqueueErrorSnackbar(e);
        }
      });
  };

  // Checking whether we need confirm password
  const isConfirmPasswordRequired =
    registrationFields &&
    registrationFields!.visible.find(field => field.id === INPUT_FIELDS.ConfirmPassword);

  return (
    <div style={styles.innerContainer}>
      <Formik
        validationSchema={
          !mfaToken
            ? registratonFieldSchema!.visible
            : registratonFieldSchema?.visible?.shape({
                mfaCode: Yup.string().required('Required').min(4, 'Too Short'),
              })
        }
        initialValues={{ ...registrationFieldInitialValues.visible, mfaCode: '' }}
        //@ts-ignore
        onSubmit={submit}
        enableReinitialize={true}
      >
        {({ handleSubmit, handleChange, setFieldValue, isSubmitting, isValid, dirty, touched, values }) => {
          const handleRegisterClick = () => {
            trackSignupSubmitted(track, {
              startTime: flowStartedAt.current,
              fieldsCompletedCount: Object.values(touched).filter(Boolean).length,
              marketingEmailOptIn: Boolean(
                marketingEmailFieldId && values[marketingEmailFieldId as keyof FormikFields],
              ),
              marketingSmsOptIn: Boolean(
                marketingSmsFieldId && values[marketingSmsFieldId as keyof FormikFields],
              ),
            });
            handleSubmit();
          };

          return (
            <Form onSubmit={handleSubmit} style={styles.registrationForm}>
              <>
                {!mfaToken
                  ? registrationFields!.visible.map((field: RegisterFields, index: any) => (
                      <RegistrationFields
                        key={index}
                        field={field}
                        fieldIndex={index}
                        showStrengthBar={
                          isConfirmPasswordRequired
                            ? field.id === INPUT_FIELDS.ConfirmPassword
                            : field.id === INPUT_FIELDS.Password
                        }
                      />
                    ))
                  : registrationFields!.visible.map((field: RegisterFields, index: any) => (
                      <RegistrationFields
                        key={index}
                        field={{ ...field, editMode: 'hidden' }}
                        fieldIndex={index}
                        showStrengthBar={
                          isConfirmPasswordRequired
                            ? field.id === INPUT_FIELDS.ConfirmPassword
                            : field.id === INPUT_FIELDS.Password
                        }
                      />
                    ))}
                {Boolean(mfaToken) && (
                  <MfaCodeField
                    label={t('field.signUp.mfaCode')}
                    onChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                )}
                {enableReCaptcha && !reCaptchaToken && (
                  <div style={styles.captchaContainer}>
                    <ReCAPTCHA
                      sitekey={String(reCaptchaSiteKey)}
                      onChange={onReCaptchaChange}
                      onErrored={onReCaptchaError}
                      size={deviceTypeMobile ? 'compact' : 'normal'}
                    />
                  </div>
                )}

                <StandardButton
                  label={t(mfaToken ? 'verify' : 'button.register')}
                  containerStyle={styles.button}
                  disabled={Boolean(isSubmitting || !isValid || !dirty)}
                  themeKey="signInSubmitButton"
                  onClick={handleRegisterClick}
                />

                <ScreenLink
                  label={t('link.signIn.prefix')}
                  actionLabel={t('link.signIn.action')}
                  url="#"
                  onClick={() => {
                    trackSigninStarted(track, { entryPoint: 'login_modal' });
                    dispatch(setCurrentModal(SIGN_IN_MODAL_ID));
                  }}
                  themeKeyLabel="signInLink"
                  themeKeyActionLabel="signInLinkRight"
                />
              </>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const styles: Styles = {
  passwordStrengthBar: {
    marginTop: '1rem',
    fontSize: '0.7rem',
  },
  modalDesktopContainer: {
    padding: '50px 70px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    textTransform: 'uppercase',
  },
  innerContainer: {
    minHeight: 'min-content',
    marginTop: '1rem',
  },
  buttonContainer: {
    display: 'flex',
    paddingTop: '1rem',
    paddingBottom: '1rem',
    justifyContent: 'space-between',
  },
  registrationForm: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    justifyContent: 'space-between',
  },
  button: {
    color: 'white',
    textTransform: 'uppercase',
    // padding: '15px',
    height: 40,
    marginTop: '1.5rem',
    cursor: 'pointer',
    marginBottom: '1rem',
  },
  link: {
    marginTop: '1rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    fontSize: '0.8rem',
  },
};

export default RegisterForm;
